<template>
  <div>
    <!--
      "Minhas taxas": o estabelecimento vê a régua que governa as vendas dele,
      a posição no mês (quando há faixas) e simula "vendendo R$ X eu recebo Y".
      Os números vêm do MESMO resolver que cobra a venda real — transparência
      de verdade, não uma cópia da regra que pode divergir.
    -->
    <div class="vx-row">
      <div class="vx-col lg:w-3/5 w-full">
        <vx-card class="mb-4">
          <div class="flex justify-between items-center mb-3">
            <div>
              <h4 class="mb-0">{{ $t('my_fees.title') }}</h4>
              <small class="text-muted">{{ $t('my_fees.subtitle') }}</small>
            </div>
            <vs-chip v-if="current && current.plan && current.plan.is_default" color="primary" class="text-base">
              {{ $t('fee_plans.default') }}
            </vs-chip>
          </div>

          <div v-if="current && current.plan">
            <div class="mb-3">
              <span class="font-semibold">{{ current.plan.name }}</span>
              <small v-if="current.version" class="text-muted ml-2">
                v{{ current.version.version }}
                · {{ $t('fee_plans.effective_since', { date: $utils.format.date(current.version.effective_from) }) }}
              </small>
            </div>

            <table class="w-full" style="font-size: .95rem">
              <thead>
                <tr class="text-left text-muted">
                  <th class="pb-1">{{ $t('my_fees.what_you_sell') }}</th>
                  <th class="pb-1">{{ $t('my_fees.what_you_pay') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rule in current.rules" :key="rule.id" class="border-top">
                  <td class="py-2">{{ ruleScopeLabel(rule) }}</td>
                  <td class="py-2">
                    <template v-if="rule.pricing_mode === 'FLAT'">
                      <span class="font-semibold">{{ rateLabel(rule.fee_percent, rule.fee_fixed_cents) }}</span>
                    </template>
                    <template v-else>
                      <div v-for="tier in rule.tiers" :key="tier.id" class="mb-1">
                        <span :class="{ 'font-semibold': tier.id === rule.current_tier_id }">
                          {{ tierThresholdLabel(tier) }} → {{ rateLabel(tier.fee_percent, tier.fee_fixed_cents) }}
                          <vs-chip v-if="tier.id === rule.current_tier_id" color="success" class="text-sm ml-1">
                            {{ $t('my_fees.you_are_here') }}
                          </vs-chip>
                        </span>
                      </div>
                      <div v-if="rule.next_tier && current.position.tiers_enabled" class="mt-1">
                        <small class="text-muted">{{ nextTierLabel(rule.next_tier) }}</small>
                      </div>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <b-alert v-else-if="loaded" show variant="warning" class="p-1">
            <small>{{ $t('fee_plans.no_plan') }}</small>
          </b-alert>
        </vx-card>

        <!-- Posição no mês -->
        <vx-card v-if="current && current.position" class="mb-4">
          <h4 class="mb-3">{{ $t('my_fees.month_position') }}</h4>
          <div class="vx-row">
            <div class="vx-col sm:w-1/2 w-full">
              <small class="text-muted d-block">{{ $t('my_fees.month_gmv') }}</small>
              <span class="text-xl font-semibold hb-money">{{ formatCents(current.position.gmv_cents) }}</span>
            </div>
            <div class="vx-col sm:w-1/2 w-full">
              <small class="text-muted d-block">{{ $t('my_fees.month_sales') }}</small>
              <span class="text-xl font-semibold">{{ current.position.sales_count }}</span>
            </div>
          </div>
          <b-alert v-if="!current.position.tiers_enabled" show variant="info" class="p-1 mt-2 mb-0">
            <small>{{ $t('my_fees.tiers_disabled_hint') }}</small>
          </b-alert>
        </vx-card>

        <div class="mb-4">
          <b-button variant="outline-primary" size="sm" @click="$router.push('/financial/fees')">
            <feather-icon icon="ListIcon" class="mr-50"/>
            <span class="align-middle">{{ $t('my_fees.see_statement') }}</span>
          </b-button>
        </div>
      </div>

      <!-- Simulador -->
      <div class="vx-col lg:w-2/5 w-full">
        <vx-card>
          <h4 class="mb-1">{{ $t('my_fees.simulator') }}</h4>
          <small class="text-muted d-block mb-3">{{ $t('my_fees.simulator_hint') }}</small>

          <b-form-group :label="$t('fee_plans.simulator_amount')">
            <b-input-group prepend="R$">
              <b-form-input v-model.number="simulator.amount" type="number" min="0" step="0.01" @change="simulate"/>
            </b-input-group>
          </b-form-group>
          <b-form-group :label="$t('fee_plans.product_type')">
            <b-form-select v-model="simulator.product_type" :options="typeOptions" @change="simulate"/>
          </b-form-group>

          <div v-if="simulator.result" class="border-top pt-3">
            <template v-if="simulator.result.has_plan">
              <div class="flex justify-between mb-1">
                <small class="text-muted">{{ $t('my_fees.simulator_gross') }}</small>
                <small class="hb-money">{{ formatCents(simulator.result.amount_cents) }}</small>
              </div>
              <div class="flex justify-between mb-1">
                <small class="text-muted">{{ $t('fee_plans.simulator_fee') }}</small>
                <small class="hb-money">
                  − {{ formatCents(simulator.result.fee_amount_cents) }}
                  ({{ simulator.result.fee_percent }}%<span v-if="simulator.result.fee_fixed_cents"> + {{ formatCents(simulator.result.fee_fixed_cents) }}</span>)
                </small>
              </div>
              <div class="flex justify-between border-top pt-1">
                <span class="font-medium">{{ $t('my_fees.simulator_you_get') }}</span>
                <span class="font-semibold text-success hb-money">{{ formatCents(simulator.result.net_amount_cents) }}</span>
              </div>
            </template>
            <b-alert v-else show variant="info" class="p-1 mb-0">
              <small>{{ $t('my_fees.simulator_no_plan') }}</small>
            </b-alert>
          </div>
        </vx-card>
      </div>
    </div>
  </div>
</template>

<script>
import { BAlert, BButton, BFormGroup, BFormInput, BFormSelect, BInputGroup } from 'bootstrap-vue'
import { currencyFormatter } from '@/util/Util'
import MyFeesService from '@/services/api/MyFeesService'

const PRODUCT_TYPE_LABELS = { LES: 'Aulas', RSP: 'Espaços', SPL: 'Planos de assinatura' }

export default {
  components: { BAlert, BButton, BFormGroup, BFormInput, BFormSelect, BInputGroup },

  data() {
    return {
      service: null,
      loaded: false,
      current: null,
      simulator: {
        amount: 100,
        product_type: null,
        result: null
      }
    }
  },

  computed: {
    typeOptions() {
      return [
        { value: null, text: this.$t('fee_plans.simulator_any_type') },
        ...Object.entries(PRODUCT_TYPE_LABELS).map(([value, text]) => ({ value, text }))
      ]
    }
  },

  methods: {
    formatCents(cents) {
      return currencyFormatter((cents || 0) / 100)
    },
    ruleScopeLabel(rule) {
      switch (rule.scope_type) {
        case 'BASE': return this.$t('my_fees.any_sale')
        case 'PRODUCT_TYPE': return PRODUCT_TYPE_LABELS[rule.product_type] || rule.product_type
        case 'MODALITY': return rule.modality ? rule.modality.name : this.$t('fee_plans.scope_.MODALITY')
        case 'PRODUCT': return rule.product ? rule.product.name : this.$t('fee_plans.scope_.PRODUCT')
        default: return rule.scope_type
      }
    },
    rateLabel(percent, fixedCents) {
      const parts = []
      if (Number(percent)) parts.push(`${Number(percent)}%`)
      if (fixedCents) parts.push(currencyFormatter(fixedCents / 100))
      return parts.length ? parts.join(' + ') : this.$t('fee_plans.rate_zero')
    },
    tierThresholdLabel(tier) {
      const conditions = []
      if (tier.min_gmv_cents) conditions.push(`≥ ${currencyFormatter(tier.min_gmv_cents / 100)}/mês`)
      if (tier.min_sales_count) conditions.push(`≥ ${tier.min_sales_count} vendas/mês`)
      return conditions.length ? conditions.join(' e ') : this.$t('fee_plans.tier_floor')
    },
    nextTierLabel(nextTier) {
      const missing = []
      if (nextTier.missing_gmv_cents) missing.push(`${currencyFormatter(nextTier.missing_gmv_cents / 100)} em vendas`)
      if (nextTier.missing_sales) missing.push(`${nextTier.missing_sales} vendas`)
      return missing.length
        ? this.$t('my_fees.next_tier_missing', { missing: missing.join(' e ') })
        : ''
    },
    loadCurrent() {
      this.$vs.loading()
      this.service.current()
        .then(data => {
          this.current = data
          this.loaded = true
        })
        .catch(error => this.showErrors(null, error, this))
        .finally(() => this.$vs.loading.close())
    },
    simulate() {
      this.service.simulate({
        amount_cents: Math.round((this.simulator.amount || 0) * 100),
        product_type: this.simulator.product_type
      })
        .then(result => { this.simulator.result = result })
        .catch(() => {})
    }
  },

  beforeMount() {
    this.service = MyFeesService.build(this.$vs)
  },

  mounted() {
    this.loadCurrent()
    this.simulate()
  }
}
</script>
